import { apiRequest } from '@/services/queryClient';
import { getApiBaseUrl } from '@/config/production';
import type { Event } from '@/types/schema';
import {
    UploadBatch,
    UploadResult,
    PhotographerInfo,
    PhotoMetadata,
    UploadProgress
} from '../models/uploader.types';

const API_BASE = `${getApiBaseUrl()}/api`;

export class UploaderService {
    // Eventos
    static async getEventInfo(eventId: string): Promise<Event> {
        const response = await apiRequest('GET', `${API_BASE}/events/${eventId}`);
        const data = await response.json();
        return data.data || data;
    }

    static async getEventsByPhotographer(photographerId: string): Promise<Event[]> {
        const response = await apiRequest('GET', `${API_BASE}/events/photographer/${photographerId}`);
        const data = await response.json();
        return data.data || data;
    }

    // Batches
    static async createBatch(eventId: string, photographerId: string): Promise<UploadBatch> {
        const response = await apiRequest('POST', `${API_BASE}/photo-batches`, {
            body: JSON.stringify({ eventId, photographerId })
        });
        const data = await response.json();
        return data.data || data;
    }

    static async getBatch(batchId: string): Promise<UploadBatch> {
        const response = await apiRequest('GET', `${API_BASE}/photo-batches/${batchId}`);
        const data = await response.json();
        return data.data || data;
    }

    static async updateBatchStatus(batchId: string, status: string): Promise<UploadBatch> {
        const response = await apiRequest('PUT', `${API_BASE}/photo-batches/${batchId}`, {
            body: JSON.stringify({ status })
        });
        const data = await response.json();
        return data.data || data;
    }

    // Upload de fotos
    static async uploadPhotos(
        batchId: string,
        photos: { file: File; metadata: PhotoMetadata }[],
        onProgress?: (progress: UploadProgress) => void
    ): Promise<UploadResult> {
        const formData = new FormData();

        // Adicionar metadados do batch
        formData.append('batchId', batchId);

        // Adicionar cada foto com seus metadados
        photos.forEach((photo, index) => {
            formData.append(`photos`, photo.file);
            formData.append(`metadata_${index}`, JSON.stringify(photo.metadata));
        });

        const response = await apiRequest('POST', `${API_BASE}/photos/upload`, {
            body: formData
        });
        const data = await response.json();
        return data.data || data;
    }

    static async uploadSinglePhoto(
        batchId: string,
        file: File,
        metadata: PhotoMetadata,
        onProgress?: (progress: number) => void
    ): Promise<UploadResult> {
        const formData = new FormData();
        formData.append('batchId', batchId);
        formData.append('photo', file);
        formData.append('metadata', JSON.stringify(metadata));

        const response = await apiRequest('POST', `${API_BASE}/photos/upload`, {
            body: formData
        });
        const data = await response.json();
        return data.data || data;
    }

    // Análise de fotos
    static async analyzePhoto(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('photo', file);

        const response = await apiRequest('POST', `${API_BASE}/photos/process`, {
            body: formData
        });
        const data = await response.json();
        return data.data || data;
    }

    // Estatísticas
    static async getPhotographerStats(photographerId: string): Promise<{
        totalEvents: number;
        totalPhotos: number;
        totalBatches: number;
        recentActivity: any[];
    }> {
        const response = await apiRequest('GET', `${API_BASE}/photo-batches/photographer/${photographerId}/stats`);
        const data = await response.json();
        return data.data || data;
    }

    static async getEventStats(eventId: string): Promise<{
        totalPhotos: number;
        uploadedPhotos: number;
        pendingPhotos: number;
        batches: UploadBatch[];
    }> {
        // Usar rota de fotos do evento
        const response = await apiRequest('GET', `${API_BASE}/events/${eventId}/photos`);
        const data = await response.json();
        return {
            totalPhotos: data.data?.length || 0,
            uploadedPhotos: data.data?.length || 0,
            pendingPhotos: 0,
            batches: []
        };
    }

    // Histórico
    static async getUploadHistory(photographerId: string, limit = 10): Promise<UploadBatch[]> {
        const response = await apiRequest('GET', `${API_BASE}/photo-batches/photographer/${photographerId}`);
        const data = await response.json();
        return data.data || data;
    }

    // Limpeza
    static async cleanupFailedUploads(batchId: string): Promise<{ success: boolean; message: string }> {
        const response = await apiRequest('DELETE', `${API_BASE}/photo-batches/${batchId}`);
        const data = await response.json();
        return data.data || data;
    }
}
